import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Form, Input, Typography } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';
import { get } from 'lodash';
import styled from 'styled-components';

import { SectionHeader } from '@/components/SectionHeader';
import { GrayBox } from '@/components/Workload/tools';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import { useOptionalResourceFormContext } from '@/delivery-type-plugin/platform/resource-form';
import {
  MAAS_INPUT_PLACEHOLDER,
  type MaasDataDiskCardItem,
  MaasDataDiskCreateModal,
  MaasDataDiskItems,
  MaasDataDiskMode,
  MaasDataDiskSelectModal,
  getDataDiskMode,
  getVolumeClaimName,
  getVolumeMountPath,
  getVolumePvcTemplate,
  getVolumeStorageClassInfo,
  parseStorageQuantityToGi,
} from '@/delivery-type-plugin/plugins';

import {
  APP_STORAGE_GROUPS_FIELD,
  MAX_APP_STORAGE_GROUPS,
  groupHasConfiguredDataDisk,
  isEphemeralStorageGroup,
  isImageBackfillGroup,
  resolveAppDataVolumeName,
} from '../headless/appStorageGroups';
import { hasPresetAppStorage } from '../headless/hasPresetAppStorage';
import { useAppStorageState } from './useAppStorageState';

export interface AppStorageSettingProps {
  form: FormInstance;
  /** SKU 外层表单，用于数据盘弹窗确认后触发 DataVolume 算价 */
  skuOuterForm?: FormInstance;
  /**
   * 计价账单模型（SKU pages ResourceFormContext 与 platform context 非同一实例时需显式传入）
   */
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  /** SKU / 下单：锁定回收策略为 Delete */
  resourceEditMode?: string;
  /** 弹窗确认后询价（pages context，勿依赖 platform） */
  onPriceChange?: (outerValues: Record<string, unknown>, innerForm: FormInstance) => void | Promise<void>;
  /** 下单：SKU 预置已有存储时禁止删改 */
  lockActionsWhenFilled?: boolean;
  /** SKU preset / 初始值，用于判定是否预置存储 */
  initValues?: Record<string, unknown>;
}

const FormItem = Form.Item;

/** 应用实例存储（无模型大小约束，支持多组挂载路径与数据盘） */
export function AppStorageSetting({
  form,
  skuOuterForm,
  billModelId,
  deploymentTypeId,
  resourceEditMode,
  onPriceChange,
  lockActionsWhenFilled,
  initValues,
}: AppStorageSettingProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const resourceFormCtx = useOptionalResourceFormContext();
  const resolvedBillModelId = billModelId ?? resourceFormCtx?.billModelId;
  const resolvedDeploymentTypeId = deploymentTypeId ?? resourceFormCtx?.deploymentTypeId;
  const resolvedResourceEditMode = resourceEditMode ?? resourceFormCtx?.resourceEditMode;
  const resolvePriceChange = onPriceChange ?? resourceFormCtx?.calculatePriceData;
  /** 下单：SKU 预置已有存储时禁止删改（对齐模型存储 lockActionsWhenFilled） */
  const actionsLockedByFill = Boolean(lockActionsWhenFilled) && hasPresetAppStorage(initValues);
  const canMutateStorage = !sectionReadOnly && !actionsLockedByFill;

  const volumeToCardItem = useCallback(
    (volume: Record<string, unknown>, mountPath?: string): MaasDataDiskCardItem => {
      const mode = getDataDiskMode(volume) || MaasDataDiskMode.Existing;
      const resolvedMountPath = mountPath || getVolumeMountPath(volume);
      if (mode === MaasDataDiskMode.Create) {
        const template = getVolumePvcTemplate(volume);
        const storageRaw = get(template, 'resources.requests.storage');
        const storageGi = parseStorageQuantityToGi(storageRaw);
        return {
          mode,
          name: String(volume.name || t('components:create.data.disk')),
          storageClassInfo: getVolumeStorageClassInfo(volume),
          storageGi: storageGi ?? (storageRaw !== undefined && storageRaw !== null ? String(storageRaw) : undefined),
          accessModes: get(template, 'accessModes') as string[] | undefined,
          reclaimPolicy: volume.reclaimPolicy ? String(volume.reclaimPolicy) : undefined,
          mountPath: resolvedMountPath,
        };
      }
      return {
        mode,
        name: String(volume.name || getVolumeClaimName(volume) || ''),
        reclaimPolicy: volume.reclaimPolicy ? String(volume.reclaimPolicy) : undefined,
        mountPath: resolvedMountPath,
        claimName: getVolumeClaimName(volume),
      };
    },
    [t],
  );

  const {
    groups,
    createModalOpen,
    selectModalOpen,
    editingVolume,
    editingGroupIndex,
    defaultMountPath,
    handleSaveVolume,
    handleDeleteVolume,
    handleMountPathChange,
    handleAddGroup,
    handleRemoveGroup,
    openCreateModal,
    openSelectModal,
    openEditModal,
    closeCreateModal,
    closeSelectModal,
  } = useAppStorageState({ form });

  const editingVolumeName = useMemo(() => {
    if (editingGroupIndex == null) return resolveAppDataVolumeName(0);
    return resolveAppDataVolumeName(editingGroupIndex);
  }, [editingGroupIndex]);

  const msg = {
    add: t('components:create.data.disk'),
    add_existing: t('components:add.existing.data.disk'),
    mountPath: t('data-center:maas.model.mountPath'),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <FormItem field={APP_STORAGE_GROUPS_FIELD} hidden style={{ display: 'none', margin: 0 }}>
        <Input />
      </FormItem>

      {groups.map((group, index) => {
        const fromImage = isImageBackfillGroup(group);
        const groupLocked = fromImage || actionsLockedByFill;
        const isEphemeral = isEphemeralStorageGroup(group);
        const hasDataDisk = groupHasConfiguredDataDisk(group);
        const cardItem = hasDataDisk && group.volume ? volumeToCardItem(group.volume, group.mountPath) : undefined;
        const canMutateGroup = canMutateStorage && !fromImage;

        return (
          <Card key={group.id || `storage-${index}`} style={{ border: '1px solid var(--color-border-2)', borderRadius: '8px' }}>
            <CardTitleRow>
              <span>{t('data-center:maas.storage.configure.index', { index: index + 1 })}</span>
              {canMutateGroup ? (
                <Button type="text" size="small" status="danger" icon={<IconDelete />} onClick={() => handleRemoveGroup(index)}>
                  {t('common:delete')}
                </Button>
              ) : null}
            </CardTitleRow>
            <FormItem label={msg.mountPath}>
              <Input
                placeholder={MAAS_INPUT_PLACEHOLDER}
                value={group.mountPath}
                disabled={groupLocked || sectionReadOnly}
                onChange={(val) => handleMountPathChange(index, val)}
              />
            </FormItem>

            {isEphemeral ? (
              <Typography.Text type="secondary">{t('common:ephemeral.volume')}</Typography.Text>
            ) : hasDataDisk ? (
              <MaasDataDiskItems
                item={cardItem}
                onEdit={canMutateGroup ? () => openEditModal(index) : undefined}
                onDelete={canMutateGroup ? () => handleDeleteVolume(index) : undefined}
                isEdit={!canMutateGroup}
              />
            ) : canMutateGroup ? (
              <DashedCard>
                <Content>
                  <SectionHeader inline title={msg.add} />
                  <Button icon={<IconPlus />} type="primary" onClick={() => openCreateModal(index)} />
                </Content>
                <Content>
                  <SectionHeader inline title={msg.add_existing} />
                  <Button icon={<IconPlus />} type="primary" onClick={() => openSelectModal(index)} />
                </Content>
              </DashedCard>
            ) : null}
          </Card>
        );
      })}

      {canMutateStorage ? (
        <Button type="dashed" long icon={<IconPlus />} disabled={groups.length >= MAX_APP_STORAGE_GROUPS} onClick={handleAddGroup}>
          {t('data-center:maas.storage.add')}
        </Button>
      ) : null}

      <MaasDataDiskCreateModal
        open={createModalOpen}
        initVolume={editingVolume && getDataDiskMode(editingVolume) === MaasDataDiskMode.Create ? editingVolume : undefined}
        defaultMountPath={defaultMountPath}
        hideMountPath
        showVolumeName
        defaultVolumeName={editingVolumeName}
        billModelId={resolvedBillModelId}
        deploymentTypeId={resolvedDeploymentTypeId}
        resourceCode="DataVolume"
        resourceEditMode={resolvedResourceEditMode}
        skuOuterForm={skuOuterForm}
        resourceInnerForm={form}
        onPriceChange={resolvePriceChange}
        onClose={closeCreateModal}
        onSave={handleSaveVolume}
      />

      <MaasDataDiskSelectModal
        open={selectModalOpen}
        initVolume={editingVolume && getDataDiskMode(editingVolume) === MaasDataDiskMode.Existing ? editingVolume : undefined}
        defaultMountPath={defaultMountPath}
        hideMountPath
        showVolumeName
        defaultVolumeName={editingVolumeName}
        resourceEditMode={resolvedResourceEditMode}
        onClose={closeSelectModal}
        onSave={handleSaveVolume}
      />
    </div>
  );
}

const DashedCard = styled(Card)`
  border-style: dashed;
  .arco-card-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const CardTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-1);
  margin-bottom: 8px;
`;

const Content = styled(GrayBox)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export default AppStorageSetting;
